import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import type { IntegrationLogEntry, IntegrationLogFilters, IntegrationLogResponse } from '@/types/integration-log';
import { mockIntegrationLogs } from '@/types/integration-log';

class IntegrationLogService {
  private getBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    const envUrl = import.meta.env.VITE_API_BASE_URL;
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

    // In E2B environment, always use the detected backend URL
    // Otherwise, use the configured URL or env URL as fallback
    const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);

    return baseUrl.replace(/\/$/, '');
  }

  private async fetchAPI(endpoint: string, options?: RequestInit) {
    const baseUrl = this.getBaseUrl();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId ?? 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getLogs(
    page: number = 1,
    pageSize: number = 25,
    filters?: IntegrationLogFilters
  ): Promise<IntegrationLogResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (filters?.status && filters.status.length > 0) {
        params.append('status', filters.status.join(','));
      }
      if (filters?.source && filters.source.length > 0) {
        params.append('source', filters.source.join(','));
      }
      if (filters?.dateFrom) {
        params.append('dateFrom', filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append('dateTo', filters.dateTo);
      }
      if (filters?.resolved !== undefined) {
        params.append('resolved', filters.resolved.toString());
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }

      return await this.fetchAPI(`/api/integration-logs?${params.toString()}`);
    } catch (error) {
      console.warn('Failed to fetch integration logs from API, using mock data:', error);
      // Return filtered mock data
      return this.getMockLogs(page, pageSize, filters);
    }
  }

  private getMockLogs(
    page: number = 1,
    pageSize: number = 25,
    filters?: IntegrationLogFilters
  ): IntegrationLogResponse {
    let filteredLogs = [...mockIntegrationLogs];

    // Apply filters
    if (filters?.status && filters.status.length > 0) {
      filteredLogs = filteredLogs.filter(log => filters.status!.includes(log.status));
    }

    if (filters?.source && filters.source.length > 0) {
      filteredLogs = filteredLogs.filter(log => filters.source!.includes(log.source));
    }

    if (filters?.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) >= fromDate);
    }

    if (filters?.dateTo) {
      const toDate = new Date(filters.dateTo);
      filteredLogs = filteredLogs.filter(log => new Date(log.timestamp) <= toDate);
    }

    if (filters?.resolved !== undefined) {
      filteredLogs = filteredLogs.filter(log => log.resolved === filters.resolved);
    }

    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredLogs = filteredLogs.filter(log =>
        log.message.toLowerCase().includes(searchLower) ||
        log.details?.toLowerCase().includes(searchLower) ||
        log.assetCode?.toLowerCase().includes(searchLower) ||
        log.transactionId?.toLowerCase().includes(searchLower) ||
        log.errorCode?.toLowerCase().includes(searchLower)
      );
    }

    // Sort by timestamp descending
    filteredLogs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Paginate
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

    return {
      data: paginatedLogs,
      total: filteredLogs.length,
      page,
      pageSize,
    };
  }

  async resolveLog(logId: string, notes?: string): Promise<IntegrationLogEntry> {
    return this.fetchAPI(`/api/integration-logs/${logId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  async unresolveLog(logId: string): Promise<IntegrationLogEntry> {
    return this.fetchAPI(`/api/integration-logs/${logId}/unresolve`, {
      method: 'POST',
    });
  }

  async cleanupOldLogs(daysOld: number = 30): Promise<{ deletedCount: number }> {
    try {
      return await this.fetchAPI(`/api/integration-logs/cleanup?daysOld=${daysOld}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.warn('Failed to cleanup old integration logs:', error);
      return { deletedCount: 0 };
    }
  }
}

export const integrationLogService = new IntegrationLogService();
